import React, { useState } from 'react'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import GeneralPage from '../../components/General/GeneralPage/GeneralPage'
import Button, { ButtonType } from '../../components/General/Button/Button'
import Tile from '../../components/General/Tile/Tile'

const DETECT_DUPLICATES = gql`
    mutation BasisTypePriceDetectDuplicates {
        basisTypePriceDetectDuplicates
    }
`

const CLEANUP_DUPLICATES = gql`
    mutation BasisTypePriceCleanupDuplicates {
        basisTypePriceCleanupDuplicates
    }
`

const BasisTypePriceAdmin = () => {
    const [duplicates, setDuplicates] = useState<string[]>([])
    const [cleanupResult, setCleanupResult] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)

    const [detectDuplicates] = useMutation(DETECT_DUPLICATES, {
        onCompleted: (data) => {
            setDuplicates(data.basisTypePriceDetectDuplicates)
            setLoading(false)
        },
        onError: (error) => {
            console.error('Error detecting duplicates:', error)
            setLoading(false)
        }
    })

    const [cleanupDuplicates] = useMutation(CLEANUP_DUPLICATES, {
        onCompleted: (data) => {
            setCleanupResult(data.basisTypePriceCleanupDuplicates)
            setLoading(false)
            // Clear duplicates list since they should be cleaned up
            setDuplicates([])
        },
        onError: (error) => {
            console.error('Error cleaning up duplicates:', error)
            setLoading(false)
        }
    })

    const handleDetectDuplicates = () => {
        setLoading(true)
        setDuplicates([])
        setCleanupResult(null)
        detectDuplicates()
    }

    const handleCleanupDuplicates = () => {
        if (duplicates.length === 0) {
            alert('No duplicates detected. Run "Detect Duplicates" first.')
            return
        }

        if (confirm(`This will remove duplicate basis type prices. Are you sure you want to continue?`)) {
            setLoading(true)
            cleanupDuplicates()
        }
    }

    return (
        <GeneralPage title="Basis Type Price Admin">
            <Tile title="Duplicate Price Management">
                <div style={{ marginBottom: '20px' }}>
                    <p>Use these tools to detect and clean up duplicate basis type prices before adding database constraints.</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <Button
                        clickAction={handleDetectDuplicates}
                        buttonType={ButtonType.Primary}
                        isDisabled={loading}
                    >
                        {loading ? 'Detecting...' : 'Detect Duplicates'}
                    </Button>

                    <Button
                        clickAction={handleCleanupDuplicates}
                        buttonType={ButtonType.Danger}
                        isDisabled={loading || duplicates.length === 0}
                        style={{ marginLeft: '10px' }}
                    >
                        {loading ? 'Cleaning...' : 'Cleanup Duplicates'}
                    </Button>
                </div>

                {duplicates.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ color: 'red' }}>Duplicates Found:</h4>
                        <ul>
                            {duplicates.map((duplicate, index) => (
                                <li key={index} style={{ color: 'red', marginBottom: '5px' }}>
                                    {duplicate}
                                </li>
                            ))}
                        </ul>
                        <p style={{ fontWeight: 'bold' }}>
                            Total duplicate groups: {duplicates.length}
                        </p>
                    </div>
                )}

                {duplicates.length === 0 && !loading && cleanupResult === null && (
                    <div style={{ color: 'green', marginBottom: '20px' }}>
                        <p>Click "Detect Duplicates" to check for duplicate basis type prices.</p>
                    </div>
                )}

                {cleanupResult !== null && (
                    <div style={{ color: 'green', marginBottom: '20px' }}>
                        <h4>Cleanup Complete!</h4>
                        <p>Removed {cleanupResult} duplicate price records.</p>
                        {cleanupResult === 0 && <p>No duplicates were found to clean up.</p>}
                    </div>
                )}
            </Tile>

            <Tile title="Instructions">
                <ol>
                    <li><strong>First:</strong> Click "Detect Duplicates" to see if any duplicate basis type prices exist</li>
                    <li><strong>Second:</strong> If duplicates are found, review the list to understand what will be removed</li>
                    <li><strong>Third:</strong> Click "Cleanup Duplicates" to remove the duplicates (keeps the most recent one)</li>
                    <li><strong>Finally:</strong> Once no duplicates exist, the database migration can be safely applied</li>
                </ol>

                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                    <strong>Note:</strong> The cleanup process will keep the most recent price record for each duplicate group
                    and remove the older ones. This is based on the BasisTypePriceKey (higher = more recent).
                </div>
            </Tile>
        </GeneralPage>
    )
}

export default BasisTypePriceAdmin